import { RequestService } from '../request-service';
import type RpcModule from '@wix/ambassador-connectors-proxy-v1-connector/rpc';
import type HttpModule from '@wix/ambassador-connectors-proxy-v1-connector/http';
import {
  CredentialState,
  Exposure,
  RegistrationType,
} from '@wix/ambassador-connectors-proxy-v1-connector/types';
import type {
  DisconnectConnectorRequest,
  DisconnectConnectorResponse,
  QueryConnectorsRequest,
  QueryConnectorsResponse,
  StartConnectRequest,
  StartConnectResponse,
} from '@wix/ambassador-connectors-proxy-v1-connector/types';
import type { ConnectPayload, ConnectResult, ConnectorRef } from './types';

type RpcModuleType = typeof RpcModule;
type HttpModuleType = typeof HttpModule;

const ACTIVE_CREDENTIAL_STATE = CredentialState.ACTIVE;
const PAGE_LIMIT = 100;

const nextCursorOf = (response: QueryConnectorsResponse): string | undefined =>
  response.pagingMetadata?.hasNext
    ? response.pagingMetadata.cursors?.next ?? undefined
    : undefined;

const connectedKey = (appId: string, compId: string): string =>
  `${appId}:${compId}`;

export class ConnectorConnectionsService extends RequestService<
  RpcModuleType,
  HttpModuleType
> {
  protected async rpcModule(): Promise<RpcModuleType> {
    return import('@wix/ambassador-connectors-proxy-v1-connector/rpc');
  }

  protected async httpModule(): Promise<HttpModuleType> {
    return import('@wix/ambassador-connectors-proxy-v1-connector/http');
  }

  // Every active connection of the given connectors, scoped by app and
  // component, across all pages: one connector can hold several connections,
  // and other apps may share a component id.
  public async getConnectedKeysByRefs(
    refs: ConnectorRef[],
  ): Promise<Set<string>> {
    if (refs.length === 0) {
      return new Set();
    }
    return this.getConnectedKeys({
      'predefinedRegistration.appId': {
        $in: [...new Set(refs.map((ref) => ref.appId))],
      },
      'predefinedRegistration.componentId': {
        $in: [...new Set(refs.map((ref) => ref.compId))],
      },
    });
  }

  /**
   * @deprecated Matches by component id alone, so another app's connector
   * with the same id counts too. Use `getConnectedKeysByRefs`.
   */
  public async getConnectedKeysByComponentIds(
    componentIds: string[],
  ): Promise<Set<string>> {
    if (componentIds.length === 0) {
      return new Set();
    }
    return this.getConnectedKeys({
      'predefinedRegistration.componentId': { $in: componentIds },
    });
  }

  private async getConnectedKeys(
    filter: Record<string, unknown>,
  ): Promise<Set<string>> {
    const keys = new Set<string>();
    let cursor: string | undefined;
    do {
      const response = await this.queryConnectors({
        filter: { ...filter, credentialState: ACTIVE_CREDENTIAL_STATE },
        cursor,
      });
      for (const connector of response.connectors ?? []) {
        const registration = connector.predefinedRegistration;
        if (registration?.appId && registration.componentId) {
          keys.add(connectedKey(registration.appId, registration.componentId));
        }
      }
      cursor = nextCursorOf(response);
    } while (cursor);

    return keys;
  }

  public filterConnected = async (
    refs: ConnectorRef[],
  ): Promise<ConnectorRef[]> => {
    const connectedKeys = await this.getConnectedKeysByRefs(refs);
    return refs.filter((ref) =>
      connectedKeys.has(connectedKey(ref.appId, ref.compId)),
    );
  };

  public async queryConnections({
    limit,
    cursor,
  }: {
    limit?: number;
    cursor?: string;
  }): Promise<{
    connectors: { appId: string; compId: string; id: string }[];
    hasNext: boolean;
    nextCursor?: string;
  }> {
    const response = await this.queryConnectors({
      filter: { credentialState: ACTIVE_CREDENTIAL_STATE },
      cursor,
      limit,
    });
    const connectors = (response.connectors ?? []).flatMap((c) =>
      c.predefinedRegistration?.appId &&
      c.predefinedRegistration.componentId &&
      c.id
        ? [
            {
              appId: c.predefinedRegistration.appId,
              compId: c.predefinedRegistration.componentId,
              id: c.id,
            },
          ]
        : [],
    );
    return {
      connectors,
      hasNext: !!response.pagingMetadata?.hasNext,
      nextCursor: nextCursorOf(response),
    };
  }

  // Every active connection of the connector, across all pages: one connector
  // can hold several, and a disconnect that leaves one keeps it connected.
  public async findActiveConnectorIds(
    appId: string,
    compId: string,
  ): Promise<string[]> {
    const ids: string[] = [];
    let cursor: string | undefined;
    do {
      const response = await this.queryConnectors({
        filter: {
          'predefinedRegistration.appId': appId,
          'predefinedRegistration.componentId': compId,
          credentialState: ACTIVE_CREDENTIAL_STATE,
        },
        cursor,
      });
      for (const connector of response.connectors ?? []) {
        if (connector.id) {
          ids.push(connector.id);
        }
      }
      cursor = nextCursorOf(response);
    } while (cursor);

    return ids;
  }

  public async findConnectorId(
    appId: string,
    compId: string,
  ): Promise<string | undefined> {
    const response = await this.queryConnectors({
      // A revoked row of the same connector may come first; deleting it would
      // leave the active connection in place.
      filter: {
        'predefinedRegistration.appId': appId,
        'predefinedRegistration.componentId': compId,
        credentialState: ACTIVE_CREDENTIAL_STATE,
      },
      limit: 1,
    });
    return response.connectors?.[0]?.id ?? undefined;
  }

  public async startConnect({
    compId,
    appId,
    exposure,
    redirectUrl,
  }: ConnectPayload): Promise<ConnectResult> {
    const response = await this.request<
      StartConnectRequest,
      StartConnectResponse
    >({
      methodName: 'startConnect',
      payload: {
        predefinedRegistration: { appId, componentId: compId },
        registrationType: RegistrationType.PREDEFINED,
        ...(exposure ? { exposure: Exposure[exposure] } : {}),
        ...(redirectUrl ? { returnUrl: redirectUrl } : {}),
      },
    });
    return {
      authorizationUrl: response.redirect?.url,
      isCompleted: !!response.completed,
    };
  }

  public async deleteConnector(connectorId: string): Promise<void> {
    await this.request<DisconnectConnectorRequest, DisconnectConnectorResponse>(
      {
        methodName: 'disconnectConnector',
        payload: { connectorId },
      },
    );
  }

  private async queryConnectors({
    filter,
    cursor,
    limit = PAGE_LIMIT,
  }: {
    filter?: Record<string, unknown>;
    cursor?: string;
    limit?: number;
  }): Promise<QueryConnectorsResponse> {
    return this.request<QueryConnectorsRequest, QueryConnectorsResponse>({
      methodName: 'queryConnectors',
      payload: {
        query: {
          ...(filter ? { filter } : {}),
          cursorPaging: { limit, ...(cursor ? { cursor } : {}) },
        },
      },
    });
  }
}
